// Layer: presentation. Identity merge review (PRD §6.3).
//
// The panel is deliberately blunt about the asymmetry. Missing a merge greets a
// returning guest as new, which is embarrassing and invisible to anyone but us. A
// false merge shows one guest another guest's stay history, which is a personal-data
// breach. So
// "Different people" is the low-risk default action, merging requires picking which
// profile survives, and nothing here happens on a single mis-tap.
import { useCallback, useEffect, useState } from "react";
import type { StoriaApi } from "../application/ports";
import type { MergeCandidate } from "../domain/types";
import { ErrorPanel, messageOf } from "./ShiftView";

interface Props {
  api: StoriaApi;
}

export function MergeReview({ api }: Props) {
  const [candidates, setCandidates] = useState<MergeCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .loadMergeCandidates()
      .then(setCandidates)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api]);

  useEffect(load, [load]);

  const decide = async (
    candidate: MergeCandidate,
    merge: boolean,
    keepId?: string,
  ): Promise<void> => {
    setBusy(candidate.id);
    setError(null);
    try {
      await api.resolveMerge(candidate.id, merge, keepId);
      setCandidates((current) =>
        current ? current.filter((c) => c.id !== candidate.id) : current,
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  if (error && !candidates) return <ErrorPanel message={error} onRetry={load} />;
  if (!candidates) {
    return <p className="muted" role="status">Loading merge candidates…</p>;
  }

  return (
    <main aria-label="Identity review" className="stack">
      <header className="panel-head">
        <h2>Possible duplicates</h2>
        <p className="muted">{candidates.length} awaiting review</p>
      </header>

      <p className="muted">
        STORIA never merges guests automatically. Merging shows one guest the other&rsquo;s
        stay history, so it needs your eyes.
      </p>

      {error ? <div className="alert" role="alert">{error}</div> : null}

      {candidates.length === 0 ? (
        <p className="muted">Nothing to review.</p>
      ) : (
        <ul className="action-list">
          {candidates.map((candidate) => (
            <li key={candidate.id} className="action">
              <div className="action-head">
                <strong>Possible duplicate</strong>
                <span className="badge">
                  {(candidate.confidence * 100).toFixed(0)}% similar
                </span>
              </div>
              <ul className="why">
                <li className="mono">{candidate.candidate_a_id}</li>
                <li className="mono">{candidate.candidate_b_id}</li>
                {Object.entries(candidate.evidence).map(([key, value]) => (
                  <li key={key}>
                    {key}: {value}
                  </li>
                ))}
              </ul>
              <div className="actions">
                {/* Least destructive first, and the only single-tap option. */}
                <button
                  type="button"
                  className="primary"
                  disabled={busy === candidate.id}
                  onClick={() => void decide(candidate, false)}
                >
                  Different people
                </button>
                <button
                  type="button"
                  disabled={busy === candidate.id}
                  onClick={() =>
                    void decide(candidate, true, candidate.candidate_a_id)
                  }
                >
                  Merge, keep the first
                </button>
                <button
                  type="button"
                  disabled={busy === candidate.id}
                  onClick={() =>
                    void decide(candidate, true, candidate.candidate_b_id)
                  }
                >
                  Merge, keep the second
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
